import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@byscott-io/corebyscott';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apps, products, productLevels, features, quota, settings } from '../api/client';

const BILLING_TYPES = [
  { value: 'flat', label: 'Flat Rate' },
  { value: 'per_user', label: 'Per User' },
  { value: 'usage', label: 'Usage Based' },
  { value: 'tiered', label: 'Tiered' },
];

export default function LevelDetailPage() {
  const { appId, productId, levelId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [product, setProduct] = useState(null);
  const [level, setLevel] = useState(null);
  const [appFeatures, setAppFeatures] = useState([]);
  const [appQuota, setAppQuota] = useState([]);
  const [appSettings, setAppSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('pricing');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [appRes, productRes, levelRes, featuresRes, quotaRes, settingsRes] = await Promise.all([
        apps.get(appId),
        products.get(appId, productId),
        productLevels.get(appId, productId, levelId),
        features.list(appId),
        quota.list(appId),
        settings.list(appId),
      ]);
      setApp(appRes.data.app);
      setProduct(productRes.data);
      setLevel(levelRes.data);
      setAppFeatures(featuresRes.data);
      setAppQuota(quotaRes.data);
      setAppSettings(settingsRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [appId, productId, levelId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async () => {
    try {
      await productLevels.delete(appId, productId, levelId);
      navigate(`/app/apps/${appId}/products/${productId}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete level');
    }
  };

  const handleToggleFeature = async (feature, currentlyEnabled) => {
    try {
      if (level.features?.some(f => f.feature_id === feature.id)) {
        if (currentlyEnabled) {
          // Update to disabled
          await productLevels.addFeature(appId, productId, levelId, feature.id, false);
        } else {
          // Remove entirely
          await productLevels.removeFeature(appId, productId, levelId, feature.id);
        }
      } else {
        // Add as enabled
        await productLevels.addFeature(appId, productId, levelId, feature.id, true);
      }
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update feature');
    }
  };

  const handleUpdateQuotum = async (quotum, value) => {
    try {
      if (value === '' || value === null) {
        await productLevels.removeQuotum(appId, productId, levelId, quotum.id);
      } else {
        await productLevels.addQuotum(appId, productId, levelId, quotum.id, parseInt(value));
      }
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update quota');
    }
  };

  const handleUpdateSetting = async (setting, value) => {
    try {
      if (value === '' || value === null) {
        await productLevels.removeSetting(appId, productId, levelId, setting.id);
      } else {
        await productLevels.addSetting(appId, productId, levelId, setting.id, value);
      }
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update setting');
    }
  };

  const handleSetPrice = async (billingInterval, priceCents, perUserPriceCents) => {
    try {
      await productLevels.setPrice(appId, productId, levelId, {
        billing_interval: billingInterval,
        price_cents: priceCents,
        price_per_user_cents: perUserPriceCents || 0,
      });
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to set price');
    }
  };

  const handleRemovePrice = async (billingInterval) => {
    try {
      await productLevels.removePrice(appId, productId, levelId, billingInterval);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove price');
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-500">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="mb-6 text-sm">
        <Link to="/app/apps" className="text-blue-600 hover:text-blue-800">Apps</Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}`} className="text-blue-600 hover:text-blue-800">{app?.name}</Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}/products`} className="text-blue-600 hover:text-blue-800">Products</Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}/products/${productId}`} className="text-blue-600 hover:text-blue-800">{product?.name}</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-600">{level?.name}</span>
      </div>

      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{level.name}</h1>
            <span className="px-2 py-0.5 text-sm rounded bg-blue-100 text-blue-800">
              {BILLING_TYPES.find(t => t.value === level.billing_type)?.label}
            </span>
          </div>
          <p className="text-gray-500">/{level.slug}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditing(true)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            Edit
          </button>
          <button onClick={() => setShowDeleteConfirm(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50">
            Delete
          </button>
        </div>
      </div>

      {/* Level Details */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Billing Type</label>
            <p className="mt-1 text-lg font-semibold">
              {BILLING_TYPES.find(t => t.value === level.billing_type)?.label}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Trial Days</label>
            <p className="mt-1 text-lg font-semibold">{level.trial_days || 0}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Status</label>
            <div className="mt-1">
              {level.active ? (
                <span className="px-2 py-1 text-sm rounded-full bg-green-100 text-green-800">Active</span>
              ) : (
                <span className="px-2 py-1 text-sm rounded-full bg-gray-100 text-gray-800">Inactive</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {['pricing', 'features', 'quota', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'pricing'
                ? `Pricing (${level.prices?.length || 0})`
                : tab === 'features'
                  ? `Features (${level.features?.length || 0})`
                  : tab === 'quota'
                    ? `Quota (${level.quota?.length || 0})`
                    : `Settings (${level.settings?.length || 0})`
              }
            </button>
          ))}
        </nav>
      </div>

      {/* Pricing Tab */}
      {activeTab === 'pricing' && (
        <PricingTab
          level={level}
          onSetPrice={handleSetPrice}
          onRemovePrice={handleRemovePrice}
        />
      )}

      {/* Features Tab */}
      {activeTab === 'features' && (
        <div className="bg-white shadow rounded-lg">
          {appFeatures.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No features defined for this app. <Link to={`/app/apps/${appId}/features`} className="text-blue-600 hover:text-blue-800">Add features</Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {appFeatures.map((feature) => {
                const levelFeature = level.features?.find(f => f.feature_id === feature.id);
                const isEnabled = levelFeature?.enabled;
                return (
                  <div key={feature.id} className="p-4 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{feature.name}</p>
                      <p className="text-sm text-gray-500">{feature.key}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleFeature(feature, isEnabled)}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          isEnabled
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {isEnabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Quota Tab */}
      {activeTab === 'quota' && (
        <div className="bg-white shadow rounded-lg">
          {appQuota.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No quota defined for this app. <Link to={`/app/apps/${appId}/quota`} className="text-blue-600 hover:text-blue-800">Add quota</Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {appQuota.map((q) => {
                const levelQuotum = level.quota?.find(lq => lq.quotum_id === q.id);
                return (
                  <div key={q.id} className="p-4 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{q.name}</p>
                      <p className="text-sm text-gray-500">{q.key}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={levelQuotum?.value ?? ''}
                        onChange={(e) => handleUpdateQuotum(q, e.target.value)}
                        placeholder="Unlimited"
                        className="w-32 border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white shadow rounded-lg">
          {appSettings.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No settings defined for this app. <Link to={`/app/apps/${appId}/settings`} className="text-blue-600 hover:text-blue-800">Add settings</Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {appSettings.map((s) => {
                const levelSetting = level.settings?.find(ls => ls.setting_id === s.id);
                return (
                  <div key={s.id} className="p-4 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{s.name}</p>
                      <p className="text-sm text-gray-500">{s.key}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={levelSetting?.value ?? ''}
                        onChange={(e) => handleUpdateSetting(s, e.target.value)}
                        placeholder={JSON.stringify(s.default_value) || 'Default'}
                        className="w-48 border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <Modal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Level"
        size="md"
        footer={
          <>
            <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button onClick={handleDelete} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600">
          Are you sure you want to delete <strong>{level.name}</strong>?
        </p>
      </Modal>

      {editing && (
        <EditLevelModal
          appId={appId}
          productId={productId}
          level={level}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function EditLevelModal({ appId, productId, level, onClose, onSaved }) {
  const [name, setName] = useState(level.name);
  const [billingType, setBillingType] = useState(level.billing_type);
  const [trialDays, setTrialDays] = useState(level.trial_days?.toString() || '0');
  const [active, setActive] = useState(level.active);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await productLevels.update(appId, productId, level.id, {
        name,
        billing_type: billingType,
        trial_days: parseInt(trialDays || 0),
        active,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update level');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Edit Level" size="lg">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Level Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Billing Type</label>
            <select
              value={billingType}
              onChange={(e) => setBillingType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {BILLING_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Pricing is configured in the Pricing tab for each billing interval.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Trial Days</label>
            <input
              type="number"
              min="0"
              value={trialDays}
              onChange={(e) => setTrialDays(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
    </Modal>
  );
}

function PricingTab({ level, onSetPrice, onRemovePrice }) {
  const [monthlyPrice, setMonthlyPrice] = useState('');
  const [monthlyPerUser, setMonthlyPerUser] = useState('');
  const [yearlyPrice, setYearlyPrice] = useState('');
  const [yearlyPerUser, setYearlyPerUser] = useState('');
  const [saving, setSaving] = useState(null);

  const isPerUser = level.billing_type === 'per_user';

  // Initialize form from existing prices
  useEffect(() => {
    const monthly = level.prices?.find(p => p.billing_interval === 'month');
    const yearly = level.prices?.find(p => p.billing_interval === 'year');

    if (monthly) {
      setMonthlyPrice((monthly.price_cents / 100).toString());
      setMonthlyPerUser(((monthly.price_per_user_cents || 0) / 100).toString());
    }
    if (yearly) {
      setYearlyPrice((yearly.price_cents / 100).toString());
      setYearlyPerUser(((yearly.price_per_user_cents || 0) / 100).toString());
    }
  }, [level.prices]);

  const handleSavePrice = async (interval) => {
    const price = interval === 'month' ? monthlyPrice : yearlyPrice;
    const perUser = interval === 'month' ? monthlyPerUser : yearlyPerUser;

    // For per-user plans, we use the per-user price; for flat, we use the base price
    const priceToValidate = isPerUser ? perUser : price;

    if (!priceToValidate || parseFloat(priceToValidate) < 0) {
      alert('Please enter a valid price');
      return;
    }

    try {
      setSaving(interval);
      // For per-user plans: price_cents is 0, per_user_cents is the actual price
      // For flat plans: price_cents is the actual price, per_user_cents is 0
      await onSetPrice(
        interval,
        isPerUser ? 0 : Math.round(parseFloat(price || 0) * 100),
        isPerUser ? Math.round(parseFloat(perUser || 0) * 100) : 0
      );
    } finally {
      setSaving(null);
    }
  };

  const handleRemovePrice = async (interval) => {
    if (!confirm(`Remove ${interval}ly pricing?`)) return;
    try {
      setSaving(interval);
      await onRemovePrice(interval);
      if (interval === 'month') {
        setMonthlyPrice('');
        setMonthlyPerUser('');
      } else {
        setYearlyPrice('');
        setYearlyPerUser('');
      }
    } finally {
      setSaving(null);
    }
  };

  const monthlyExists = level.prices?.find(p => p.billing_interval === 'month');
  const yearlyExists = level.prices?.find(p => p.billing_interval === 'year');

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-6">Pricing Configuration</h3>

      <p className="text-sm text-gray-600 mb-6">
        Configure explicit pricing for each billing interval.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Monthly Pricing */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium mb-4">Monthly Pricing</h4>

          <div className="space-y-3">
            {isPerUser ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price Per User ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={monthlyPerUser}
                  onChange={(e) => setMonthlyPerUser(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
                <p className="mt-1 text-xs text-gray-500">Each user pays this amount per month</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={monthlyPrice}
                  onChange={(e) => setMonthlyPrice(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => handleSavePrice('month')}
                disabled={saving === 'month' || (isPerUser ? !monthlyPerUser : !monthlyPrice)}
                className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving === 'month' ? 'Saving...' : monthlyExists ? 'Update' : 'Save'}
              </button>
              {monthlyExists && (
                <button
                  onClick={() => handleRemovePrice('month')}
                  disabled={saving === 'month'}
                  className="px-3 py-2 border border-red-300 text-red-600 text-sm rounded hover:bg-red-50"
                >
                  Remove
                </button>
              )}
            </div>

          </div>
        </div>

        {/* Yearly Pricing */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium mb-4">Yearly Pricing</h4>

          <div className="space-y-3">
            {isPerUser ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price Per User ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={yearlyPerUser}
                  onChange={(e) => setYearlyPerUser(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
                <p className="mt-1 text-xs text-gray-500">Each user pays this amount per year</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={yearlyPrice}
                  onChange={(e) => setYearlyPrice(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => handleSavePrice('year')}
                disabled={saving === 'year' || (isPerUser ? !yearlyPerUser : !yearlyPrice)}
                className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving === 'year' ? 'Saving...' : yearlyExists ? 'Update' : 'Save'}
              </button>
              {yearlyExists && (
                <button
                  onClick={() => handleRemovePrice('year')}
                  disabled={saving === 'year'}
                  className="px-3 py-2 border border-red-300 text-red-600 text-sm rounded hover:bg-red-50"
                >
                  Remove
                </button>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
